import { useEffect, useMemo, useState, useCallback } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import {
  formatAmount,
  generateTDHPBalanceSheet,
  validateBalanceSheetTotals,
  validateTDHPStructure
} from './tdhpTemplate';
import { exportToExcel, exportToPdf } from './balanceSheetExports';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './BalanceSheetPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    forbidden: 'Bu sirket icin bilanco verilerine erisim yetkiniz yok.',
    fetchFailed: 'Bilanco verisi alinamadi.',
    fetchError: 'Bilanco verisi yuklenirken hata olustu.',
    companyNotSelected: 'Sirket secilmedi',
    periodNotSelected: 'Donem secilmedi',
    headerDescription: '{{company}} | {{period}} icin TDHP uyumlu bilanco ozeti.',
    title: 'Bilanco Intelligence',
    showZero: 'Sifir satirlari goster',
    hideZero: 'Sifir satirlari gizle',
    refreshing: 'Yenileniyor...',
    refreshData: 'Verileri Yenile',
    exportExcelTitle: 'Excel olarak indir',
    exportPdfTitle: 'PDF olarak indir',
    balanceScore: 'Denge Skoru',
    balanced: 'Dengeli',
    watch: 'Izlenmeli',
    critical: 'Kritik',
    totalAssets: 'Toplam Aktif',
    assetBase: 'Varlik tabani',
    totalLiabilities: 'Toplam Kaynak',
    liabilityLoad: 'Yukumluluk yuklenimi %{{value}}',
    dataTrust: 'Data Trust',
    missingCode: 'Eksik kod: {{count}}',
    currentAssetsTotal: 'Donen Varliklar Toplami',
    nonCurrentAssetsTotal: 'Duran Varliklar Toplami',
    shortTermLiabilitiesTotal: 'KVYK Toplami',
    longTermLiabilitiesTotal: 'UVYK Toplami',
    equity: 'Oz Sermaye',
    info: 'Sirket ve donem secildikten sonra bilanco verisi gosterilir.',
    loading: 'Bilanco verisi hazirlaniyor...',
    errorTitle: 'Bilanco verisi alinamadi.',
    errorHint: 'Olasi sebepler: servis erisimi, donem verisi eksigi veya yetki.',
    emptyTitle: 'Secilen kombinasyon icin bilanco satirlari bulunamadi.',
    emptyHint: 'Kebir mizan olusumu ve donem secimini kontrol edin.',
    closingMissingTitle: 'Balance-closing uygulanmadi',
    closingMissingHint: '590/591 sifir gorunuyor olabilir; bilanconun muhasebe olarak tamamlanmasi icin financial-analysis/balance-closing adimini calistirin.',
    validationMissing: 'TDHP yapisinda {{count}} eksik kebir kodu var.',
    validationMissingList: 'Eksik kodlar: {{codes}}',
    accountDesc: 'Hesap / Aciklama',
    amountTl: 'Tutar (TL)',
    totalAssetsLabel: 'TOPLAM VARLIKLAR',
    totalResourcesLabel: 'TOPLAM KAYNAKLAR',
    diffLabel: 'FARK',
    balanceValid: 'Bilanco dengeli: Aktif = Pasif',
    balanceInvalid: 'Bilanco dengeli degil: Aktif ({{assets}}) != Pasif ({{liabilities}})',
    report: 'Rapor',
    date: 'Tarih',
    currency: 'Para Birimi'
  },
  'en-US': {
    forbidden: 'You do not have permission to access balance sheet data for this company.',
    fetchFailed: 'Balance sheet data could not be fetched.',
    fetchError: 'An error occurred while loading balance sheet data.',
    companyNotSelected: 'Company not selected',
    periodNotSelected: 'Period not selected',
    headerDescription: 'TDHP-compatible balance sheet summary for {{company}} | {{period}}.',
    title: 'Balance Sheet Intelligence',
    showZero: 'Show zero lines',
    hideZero: 'Hide zero lines',
    refreshing: 'Refreshing...',
    refreshData: 'Refresh Data',
    exportExcelTitle: 'Download as Excel',
    exportPdfTitle: 'Download as PDF',
    balanceScore: 'Balance Score',
    balanced: 'Balanced',
    watch: 'Watch',
    critical: 'Critical',
    totalAssets: 'Total Assets',
    assetBase: 'Asset base',
    totalLiabilities: 'Total Resources',
    liabilityLoad: 'Liability load %{{value}}',
    dataTrust: 'Data Trust',
    missingCode: 'Missing codes: {{count}}',
    currentAssetsTotal: 'Current Assets Total',
    nonCurrentAssetsTotal: 'Non-Current Assets Total',
    shortTermLiabilitiesTotal: 'Short-Term Liabilities Total',
    longTermLiabilitiesTotal: 'Long-Term Liabilities Total',
    equity: 'Equity',
    info: 'Balance sheet data will be shown after selecting company and period.',
    loading: 'Preparing balance sheet data...',
    errorTitle: 'Balance sheet data could not be fetched.',
    errorHint: 'Possible reasons: service access, missing period data, or permissions.',
    emptyTitle: 'No balance sheet lines found for selected combination.',
    emptyHint: 'Check general ledger trial balance generation and period selection.',
    closingMissingTitle: 'Balance-closing not applied',
    closingMissingHint: '590/591 may appear as zero; run the financial-analysis/balance-closing step to complete the balance sheet posting.',
    validationMissing: 'TDHP structure has {{count}} missing ledger codes.',
    validationMissingList: 'Missing codes: {{codes}}',
    accountDesc: 'Account / Description',
    amountTl: 'Amount (TRY)',
    totalAssetsLabel: 'TOTAL ASSETS',
    totalResourcesLabel: 'TOTAL LIABILITIES + EQUITY',
    diffLabel: 'DIFFERENCE',
    balanceValid: 'Balance sheet is balanced: Assets = Liabilities',
    balanceInvalid: 'Balance sheet is not balanced: Assets ({{assets}}) != Liabilities ({{liabilities}})',
    report: 'Report',
    date: 'Date',
    currency: 'Currency'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseErrorMessage = async (response, fallback, t) => {
  if (response.status === 403) return t('forbidden');
  try {
    const payload = await response.json();
    if (payload?.message) return payload.message;
  } catch {
    // ignore
  }
  try {
    const text = await response.text();
    if (text) return text;
  } catch {
    // ignore
  }
  return fallback;
};

function BalanceSheetPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [lines, setLines] = useState([]);
  const [stats, setStats] = useState({ assetsTotal: 0, liabilitiesTotal: 0, diff: 0 });
  const [closingState, setClosingState] = useState({ applied: true, warning: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasData, setHasData] = useState(false);
  const [hideZeroLines, setHideZeroLines] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!selection.companyId || !selection.periodId) {
        setLines([]);
        setStats({ assetsTotal: 0, liabilitiesTotal: 0, diff: 0 });
        setHasData(false);
        setClosingState({ applied: true, warning: '' });
        setError('');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
        const response = await fetch(`${API_BASE_URL}/api/financials/balance-sheet?${params.toString()}`, { headers: buildHeaders() });
        if (!response.ok) {
          const errorMessage = await parseErrorMessage(response, t('fetchFailed'), t);
          throw new Error(errorMessage);
        }

        const payload = await response.json().catch(() => ({}));
        const balanceSheet = Array.isArray(payload.balanceSheet) ? payload.balanceSheet : [];
        setLines(balanceSheet);
        setHasData(Boolean(payload.hasData));
        setClosingState({
          applied: Boolean(payload.balanceClosingApplied),
          warning: payload.balanceClosingWarning || ''
        });
        setStats({
          assetsTotal: Number(payload.assetsTotal ?? 0),
          liabilitiesTotal: Number(payload.liabilitiesTotal ?? 0),
          diff: Number(payload.diff ?? (payload.assetsTotal ?? 0) - (payload.liabilitiesTotal ?? 0))
        });
      } catch (caught) {
        setError(caught?.message || t('fetchError'));
        setLines([]);
        setHasData(false);
        setClosingState({ applied: true, warning: '' });
        setStats({ assetsTotal: 0, liabilitiesTotal: 0, diff: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selection.companyId, selection.periodId, refreshNonce, t]);

  const handleRefresh = () => setRefreshNonce((prev) => prev + 1);

  const exportFilename = useMemo(() => {
    const company = selection.company?.name?.replace(/[^a-zA-Z0-9\u00C0-\u024F]/g, '_') || 'company';
    const period = selection.period?.code || selection.period?.description?.replace(/[^a-zA-Z0-9]/g, '_') || 'period';
    return `balance_sheet_${company}_${period}`;
  }, [selection.company, selection.period]);

  const accountPayload = useMemo(() => lines.map((line) => ({
    AccountCode: line.accountCode,
    AccountName: line.accountName,
    display_balance: Number(line.displayBalance ?? 0),
    Balance: Number(line.rawBalance ?? 0)
  })), [lines]);

  const templateData = useMemo(() => {
    if (accountPayload.length === 0) return null;
    return generateTDHPBalanceSheet(accountPayload, hideZeroLines);
  }, [accountPayload, hideZeroLines]);

  const structureValidation = useMemo(() => {
    if (!templateData) return null;
    return validateTDHPStructure(templateData);
  }, [templateData]);

  const balanceValidation = useMemo(() => {
    if (!templateData) return null;
    return validateBalanceSheetTotals(templateData);
  }, [templateData]);

  const companyLabel = selection.company?.name || t('companyNotSelected');
  const periodLabel = selection.period?.code || selection.period?.description || t('periodNotSelected');
  const headerDescription = t('headerDescription').replace('{{company}}', companyLabel).replace('{{period}}', periodLabel);

  const handleExportExcel = () => {
    if (templateData) exportToExcel(templateData, exportFilename);
  };

  const handleExportPdf = () => {
    if (templateData) exportToPdf(templateData, exportFilename, headerDescription);
  };

  const readyToShowTable = Boolean(templateData && hasData);
  const balanceAbs = Math.abs(Number(stats.diff || 0));
  const baseTotal = Math.max(1, Math.abs(Number(stats.assetsTotal || 0)));
  const balanceScore = clamp(100 - ((balanceAbs / baseTotal) * 1000), 0, 100);
  const balanceVerdict = balanceScore >= 80 ? t('balanced') : balanceScore >= 60 ? t('watch') : t('critical');

  const liabilityLoad = stats.assetsTotal === 0 ? 0 : clamp((Math.abs(stats.liabilitiesTotal) / Math.abs(stats.assetsTotal)) * 100, 0, 200);
  const dataTrust = clamp(100 - ((structureValidation?.missingCodes?.length || 0) * 3), 0, 100);

  const groupTotals = useMemo(() => {
    const assetsBlocks = templateData?.sections?.[0]?.blocks || [];
    const liabilitiesBlocks = templateData?.sections?.[1]?.blocks || [];

    return {
      currentAssets: Number(assetsBlocks.find((block) => block.blockCode === 'I')?.blockSubtotal || 0),
      nonCurrentAssets: Number(assetsBlocks.find((block) => block.blockCode === 'II')?.blockSubtotal || 0),
      shortTermLiabilities: Number(liabilitiesBlocks.find((block) => block.blockCode === 'I')?.blockSubtotal || 0),
      longTermLiabilities: Number(liabilitiesBlocks.find((block) => block.blockCode === 'II')?.blockSubtotal || 0),
      equity: Number(liabilitiesBlocks.find((block) => block.blockCode === 'III')?.blockSubtotal || 0)
    };
  }, [templateData]);

  const renderRows = () => {
    if (!templateData) return null;

    const rows = [];
    templateData.sections.forEach((section, sectionIndex) => {
      const sectionSubtotal = section.blocks.reduce((acc, block) => acc + (block.blockSubtotal || 0), 0);
      rows.push(
        <tr key={`section-${sectionIndex}`} className="balance-sheet__section-row">
          <td>{section.title}</td>
          <td>{formatAmount(sectionSubtotal)}</td>
        </tr>
      );

      section.blocks.forEach((block, blockIndex) => {
        rows.push(
          <tr key={`block-${sectionIndex}-${blockIndex}`} className="balance-sheet__block-row">
            <td><span className="balance-sheet__block-code">{block.blockCode}</span><span className="balance-sheet__block-title">{block.blockTitle}</span></td>
            <td>{formatAmount(block.blockSubtotal ?? 0)}</td>
          </tr>
        );

        block.groups.forEach((group, groupIndex) => {
          rows.push(
            <tr key={`group-${sectionIndex}-${blockIndex}-${groupIndex}`} className="balance-sheet__group-row">
              <td>{group.groupTitle}</td>
              <td>{formatAmount(group.subtotal ?? 0)}</td>
            </tr>
          );

          group.lines.forEach((line, lineIndex) => {
            if (line.hidden) return;
            rows.push(
              <tr key={`line-${sectionIndex}-${blockIndex}-${groupIndex}-${lineIndex}`} className="balance-sheet__line-row">
                <td><span className="balance-sheet__line-code">{line.code}</span><span className="balance-sheet__line-label">{line.label}</span>{line.contra && <span className="balance-sheet__contra">(-)</span>}</td>
                <td>{formatAmount(line.amount ?? 0)}</td>
              </tr>
            );
          });
        });
      });
    });

    const totals = templateData.totals || {};
    rows.push(<tr key="total-assets" className="balance-sheet__total-row"><td>{t('totalAssetsLabel')}</td><td>{formatAmount(totals.assetsTotal ?? 0)}</td></tr>);
    rows.push(<tr key="total-liabilities" className="balance-sheet__total-row"><td>{t('totalResourcesLabel')}</td><td>{formatAmount(totals.liabilitiesEquityTotal ?? 0)}</td></tr>);
    rows.push(<tr key="total-diff" className="balance-sheet__total-row"><td>{t('diffLabel')}</td><td>{formatAmount(totals.diff ?? 0)}</td></tr>);

    return rows;
  };

  const showInfo = !selection.companyId || !selection.periodId;
  const showEmpty = !loading && !error && !readyToShowTable;

  return (
    <div className="balance-sheet card">
      <PageHeader title={t('title')} description={headerDescription} />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <div className="balance-sheet__controls">
        <button type="button" className={`btn-secondary balance-sheet__toggle ${hideZeroLines ? 'active' : ''}`} onClick={() => setHideZeroLines((prev) => !prev)} disabled={loading || !selection.companyId || !selection.periodId}>
          {hideZeroLines ? t('showZero') : t('hideZero')}
        </button>
        <button type="button" className="btn-primary" onClick={handleRefresh} disabled={loading || !selection.companyId || !selection.periodId}>
          {loading ? t('refreshing') : t('refreshData')}
        </button>
        <button type="button" className="btn-secondary" onClick={handleExportExcel} disabled={!readyToShowTable || !templateData} title={t('exportExcelTitle')}>Excel</button>
        <button type="button" className="btn-secondary" onClick={handleExportPdf} disabled={!readyToShowTable || !templateData} title={t('exportPdfTitle')}>PDF</button>
      </div>

      <section className="balance-sheet__executive-grid">
        <article className="balance-sheet__exec-card balance-sheet__gauge-card">
          <header><span>{t('balanceScore')}</span><strong>{Math.round(balanceScore)}</strong></header>
          <div className="balance-sheet__gauge" aria-hidden="true"><div className="balance-sheet__gauge-center">{Math.round(balanceScore)}</div></div>
          <footer><span className={`balance-sheet__badge balance-sheet__badge-${balanceVerdict === t('balanced') ? 'ok' : balanceVerdict === t('watch') ? 'warn' : 'risk'}`}>{balanceVerdict}</span></footer>
        </article>

        <article className="balance-sheet__exec-card">
          <header><span>{t('totalAssets')}</span><strong>{formatAmount(stats.assetsTotal)}</strong></header>
          <div className="balance-sheet__mini-bar"><span style={{ width: '100%' }} /></div>
          <footer><span>{t('assetBase')}</span></footer>
        </article>

        <article className="balance-sheet__exec-card">
          <header><span>{t('totalLiabilities')}</span><strong>{formatAmount(stats.liabilitiesTotal)}</strong></header>
          <div className="balance-sheet__mini-bar"><span style={{ width: `${clamp(liabilityLoad, 0, 100)}%` }} /></div>
          <footer><span>{t('liabilityLoad').replace('{{value}}', String(Math.round(liabilityLoad)))}</span></footer>
        </article>

        <article className="balance-sheet__exec-card">
          <header><span>{t('dataTrust')}</span><strong>{Math.round(dataTrust)}%</strong></header>
          <div className="balance-sheet__mini-bar"><span style={{ width: `${dataTrust}%` }} /></div>
          <footer><span>{t('missingCode').replace('{{count}}', String(structureValidation?.missingCodes?.length || 0))}</span></footer>
        </article>
      </section>

      <section className="balance-sheet__group-totals-grid">
        <article className="balance-sheet__group-card"><span>{t('currentAssetsTotal')}</span><strong>{formatAmount(groupTotals.currentAssets)}</strong></article>
        <article className="balance-sheet__group-card"><span>{t('nonCurrentAssetsTotal')}</span><strong>{formatAmount(groupTotals.nonCurrentAssets)}</strong></article>
        <article className="balance-sheet__group-card"><span>{t('shortTermLiabilitiesTotal')}</span><strong>{formatAmount(groupTotals.shortTermLiabilities)}</strong></article>
        <article className="balance-sheet__group-card"><span>{t('longTermLiabilitiesTotal')}</span><strong>{formatAmount(groupTotals.longTermLiabilities)}</strong></article>
        <article className="balance-sheet__group-card"><span>{t('equity')}</span><strong>{formatAmount(groupTotals.equity)}</strong></article>
      </section>

      {showInfo && <div className="balance-sheet__alert balance-sheet__alert--info"><p>{t('info')}</p></div>}
      {!showInfo && loading && <div className="balance-sheet__alert balance-sheet__alert--loading"><p>{t('loading')}</p></div>}
      {!showInfo && !loading && error && (
        <div className="balance-sheet__alert balance-sheet__alert--error">
          <p>{t('errorTitle')}</p>
          <p className="balance-sheet__alert-detail">{error}</p>
          <p className="balance-sheet__alert-detail">{t('errorHint')}</p>
        </div>
      )}
      {showEmpty && (
        <div className="balance-sheet__alert balance-sheet__alert--empty">
          <p>{t('emptyTitle')}</p>
          <p className="balance-sheet__alert-detail">{t('emptyHint')}</p>
        </div>
      )}
      {!showInfo && !loading && !error && !closingState.applied && (
        <div className="balance-sheet__alert balance-sheet__alert--warning">
          <p>{t('closingMissingTitle')}</p>
          <p className="balance-sheet__alert-detail">{closingState.warning || t('closingMissingHint')}</p>
        </div>
      )}

      {!showInfo && !loading && !error && readyToShowTable && (
        <>
          {structureValidation && !structureValidation.isValid && (
            <div className="balance-sheet__validation">
              <p>{t('validationMissing').replace('{{count}}', String(structureValidation.missingCodes.length))}</p>
              <p className="balance-sheet__alert-detail">{t('validationMissingList').replace('{{codes}}', structureValidation.missingCodes.join(', '))}</p>
            </div>
          )}

          <div className="balance-sheet__table-wrapper">
            <table className="balance-sheet__table">
              <thead>
                <tr>
                  <th>{t('accountDesc')}</th>
                  <th>{t('amountTl')}</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>

          <div className={`balance-sheet__balance-check ${balanceValidation?.isValid ? 'balance-sheet__balance-check--valid' : 'balance-sheet__balance-check--error'}`}>
            {balanceValidation?.isValid ? (
              <p>{t('balanceValid')}</p>
            ) : (
              <p>{t('balanceInvalid').replace('{{assets}}', formatAmount(stats.assetsTotal)).replace('{{liabilities}}', formatAmount(stats.liabilitiesTotal))}</p>
            )}
          </div>

          {templateData && (
            <div className="balance-sheet__meta">
              <p>{t('report')}: {templateData.report}</p>
              <p>{t('date')}: {templateData.asOf ? new Date(templateData.asOf).toLocaleDateString(localeKey) : '-'}</p>
              <p>{t('currency')}: {templateData.currency || 'TRY'}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BalanceSheetPage;
